/**
 * Their page, read from `GET /api/watch/:token`: the private conversation with the host across every
 * room the person is in, their answers, Sessions, and what Pinned holds. Pure, so every screen width
 * draws the same thing and it tests plainly.
 */
import { assetUrl } from "../../lib/api";
import { daysUntil, labelParts, wallClock } from "../../../shared/live-time";
import type {
  Attachment,
  HandoutConfig,
  WatchAnswer,
  WatchComment,
  WatchMessage,
  WatchPageResponse,
  WatchPin,
  WatchPost,
  WatchSeriesSession,
  WatchSession,
} from "../../../shared/types";
import { comingSession, youWords } from "../series";
import { drawThread, type Outgoing, type ThreadState } from "./thread";

export type From = "host" | "me";

/** One thing in the conversation: a chat message, an update the host added, or a reply under one. */
export interface Entry {
  /** Stable across a read turning the page's own message into the server's. */
  key: string;
  kind: "message" | "post" | "comment";
  /** The server id (the page id while the person's message is on its way). */
  id: string;
  webinar_id: string;
  from: From;
  at: number;
  message?: WatchMessage;
  post?: WatchPost;
  comment?: WatchComment;
  outgoing?: Outgoing;
  /** Replies drawn under it, oldest first. */
  replies: Entry[];
  /** The entry this one is a reply under. */
  parent?: string;
  /** Arrived since the person was last here. */
  fresh: boolean;
}

/** A quick live the person was in: it shows in the thread as the call card, not as a room. */
export interface QuickCall {
  key: string;
  webinar_id: string;
  sessionId: string;
  title: string;
  /** Unix seconds it started. */
  at: number;
  minutes: number;
  replay: "none" | "preparing" | "ready";
}

export type Line =
  | { type: "sep"; key: string; day: string; time: string; joiner: string }
  | { type: "unread"; key: string; count: number }
  | { type: "entry"; key: string; entry: Entry }
  | { type: "call"; key: string; call: QuickCall };

export interface Host {
  name: string;
  first: string;
  avatar: string | null;
}

export const firstWord = (name: string): string => name.trim().split(/\s+/)[0] ?? "";

/** The host the person talks to: the one in the room they joined last. */
export function hostOf(data: Pick<WatchPageResponse, "sessions" | "workspace_name">): Host {
  const s = data.sessions[0];
  const name = s?.host_name || data.workspace_name;
  return { name, first: firstWord(name), avatar: s?.host_avatar_url ?? null };
}

/**
 * The quick lives the person was in: a live that ran once, with no series, that is not one of their rooms
 * (those are in the left side) and not a circle's call (it shows in its circle).
 */
export function quickCallsOf(sessions: readonly WatchSession[], notThese: ReadonlySet<string>): QuickCall[] {
  const out: QuickCall[] = [];
  for (const s of sessions) {
    const live = s.live;
    if (!live || live.series || live.state !== "ended" || notThese.has(s.webinar_id)) continue;
    out.push({
      key: `call-${live.sessionId}`,
      webinar_id: s.webinar_id,
      sessionId: live.sessionId,
      title: live.topic || s.title,
      at: Math.floor(Date.parse(live.startsAt) / 1000),
      minutes: live.lengthMin,
      replay: live.replay,
    });
  }
  return out;
}

// --- the conversation ---

/** The person's message that answers a host question sits in the conversation, not under the question. */
const isReply = (m: WatchMessage) => Boolean(m.reply_to) && !m.answer_to;

/** Local comments, kept until a read shows them by id (as the page's own chat messages are). */
type LocalComments = ReadonlyMap<string, readonly WatchComment[]>;

export interface Conversation {
  lines: Line[];
  /** Every entry by key and by server id, replies too. */
  byId: Map<string, Entry>;
}

export function buildConversation(
  data: Pick<WatchPageResponse, "sessions">,
  thread: ThreadState,
  local: LocalComments,
  nowSec: number,
  calls: readonly QuickCall[] = [],
  timeZone?: string,
): Conversation {
  const roomOf = new Map<string, string>();
  const since = new Map<string, number>();
  for (const s of data.sessions) {
    since.set(s.webinar_id, s.last_visit_at);
    for (const m of s.messages) roomOf.set(m.id, s.webinar_id);
  }
  const fresh = (webinarId: string, from: From, at: number) => from === "host" && at > (since.get(webinarId) ?? nowSec);
  const byId = new Map<string, Entry>();
  const top: Entry[] = [];
  const replies: Entry[] = [];

  for (const d of drawThread(thread, nowSec)) {
    const m = d.message;
    const webinarId = d.outgoing?.webinar_id ?? roomOf.get(m.id) ?? "";
    const from: From = m.sender === "host" ? "host" : "me";
    const e: Entry = {
      key: d.outgoing?.cid ?? m.client_id ?? m.id,
      kind: "message",
      id: m.id,
      webinar_id: webinarId,
      from,
      at: m.created_at,
      message: m,
      outgoing: d.outgoing,
      replies: [],
      fresh: fresh(webinarId, from, m.created_at),
    };
    byId.set(e.id, e);
    byId.set(e.key, e);
    (isReply(m) ? replies : top).push(e);
  }

  // Why only personal: a post to the whole room is in Announcements, never in the private thread.
  for (const s of data.sessions) {
    for (const p of s.posts) {
      if (p.scope !== "personal" || byId.has(p.id)) continue;
      const e: Entry = {
        key: p.id,
        kind: "post",
        id: p.id,
        webinar_id: s.webinar_id,
        from: "host",
        at: p.created_at,
        post: p,
        replies: [],
        fresh: fresh(s.webinar_id, "host", p.created_at),
      };
      const shown = new Set(p.comments.map((c) => c.id));
      const comments = [...p.comments, ...(local.get(p.id) ?? []).filter((c) => !shown.has(c.id))];
      for (const c of comments) {
        const from: From = c.author === "host" ? "host" : "me";
        const r: Entry = {
          key: c.id,
          kind: "comment",
          id: c.id,
          webinar_id: s.webinar_id,
          from,
          at: c.created_at,
          comment: c,
          replies: [],
          parent: p.id,
          fresh: fresh(s.webinar_id, from, c.created_at),
        };
        byId.set(c.id, r);
        e.replies.push(r);
      }
      byId.set(p.id, e);
      top.push(e);
    }
  }

  // A chat reply sits under the message its thread started from.
  const rootOf = (id: string): Entry | undefined => {
    let e = byId.get(id);
    for (let hops = 0; e?.message?.reply_to && isReply(e.message) && hops < 20; hops++) e = byId.get(e.message.reply_to);
    return e;
  };
  for (const r of replies) {
    const root = r.message?.reply_to ? rootOf(r.message.reply_to) : undefined;
    if (root && root !== r) {
      r.parent = root.id;
      root.replies.push(r);
    } else top.push(r);
  }

  // Why: two sends in one second keep the order they were sent in.
  const sentAt = new Map(thread.outgoing.map((o, i) => [o.cid, i]));
  const order = (a: Entry, b: Entry) =>
    a.at - b.at || (sentAt.get(a.key) ?? -1) - (sentAt.get(b.key) ?? -1) || a.key.localeCompare(b.key);
  top.sort(order);
  for (const e of top) e.replies.sort(order);
  // The person's messages still on their way come last, in the order they were sent.
  const settled = top.filter((e) => !e.outgoing || e.outgoing.sent);
  const going = top.filter((e) => e.outgoing && !e.outgoing.sent);
  const all = [...settled, ...going];

  const lines: Line[] = [];
  const newCount = all.reduce((n, e) => n + (e.fresh ? 1 : 0) + e.replies.filter((r) => r.fresh).length, 0);
  let marked = false;
  let prevAt: number | null = null;
  const startsGroup = (at: number, key: string) => {
    if (prevAt === null || gapStarts(prevAt, at, timeZone)) lines.push({ type: "sep", key: `sep-${key}`, ...sepLabel(at, nowSec, timeZone) });
    prevAt = at;
  };
  const callsLeft = calls.toSorted((a, b) => a.at - b.at);
  const callsBefore = (at: number) => {
    while (callsLeft.length && callsLeft[0].at <= at) {
      const call = callsLeft.shift() as QuickCall;
      startsGroup(call.at, call.key);
      lines.push({ type: "call", key: call.key, call });
    }
  };
  for (const e of all) {
    callsBefore(e.outgoing && !e.outgoing.sent ? nowSec : e.at);
    // The new marker sits above the day it starts in, as the prototype draws it.
    if (!marked && newCount > 0 && (e.fresh || e.replies.some((r) => r.fresh))) {
      lines.push({ type: "unread", key: "unread", count: newCount });
      marked = true;
      prevAt = null;
    }
    startsGroup(e.at, e.key);
    lines.push({ type: "entry", key: e.key, entry: e });
  }
  callsBefore(Number.POSITIVE_INFINITY);
  return { lines, byId };
}

const HOUR = 3600;
const dayKey = (sec: number, timeZone?: string) => {
  const p = labelParts(new Date(sec * 1000).toISOString(), timeZone, "short");
  return p ? `${p.month} ${p.day}` : "";
};
/** A new group starts on a new day, or after an hour's quiet. */
function gapStarts(prevAt: number, at: number, timeZone?: string): boolean {
  return at - prevAt >= HOUR || dayKey(prevAt, timeZone) !== dayKey(at, timeZone);
}

/** "Fri, Oct 9 at 2:40 PM", "Wednesday 8:15 PM", "Yesterday 9:10 AM", "Today 9:10 AM". */
export function sepLabel(at: number, nowSec: number, timeZone?: string): { day: string; time: string; joiner: string } {
  const iso = new Date(at * 1000).toISOString();
  const short = labelParts(iso, timeZone, "short");
  const long = labelParts(iso, timeZone, "long");
  const days = -(daysUntil(iso, timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone, nowSec * 1000) ?? 99);
  const time = short?.time ?? "";
  if (days === 0) return { day: "Today", time, joiner: " " };
  if (days === 1) return { day: "Yesterday", time, joiner: " " };
  if (days > 1 && days < 7) return { day: long?.weekday ?? "", time, joiner: " " };
  return { day: short ? `${short.weekday}, ${short.month} ${short.day}` : "", time, joiner: " at " };
}

/** The day a pin was made, as the Pinned column says it: Today, Yesterday, a weekday, or the date. */
export function pinDay(at: number, nowSec: number, timeZone?: string): string {
  const l = sepLabel(at, nowSec, timeZone);
  if (l.joiner === " ") return l.day;
  const short = labelParts(new Date(at * 1000).toISOString(), timeZone, "short");
  return short ? `${short.month} ${short.day}` : "";
}

// --- what a message or update shows ---

/** The words of an update's note, without its markup. */
export function plainText(html: string | null): string {
  if (!html) return "";
  return html
    .replace(/<\s*br\s*\/?>/gi, "\n")
    .replace(/<\/\s*(p|div|li|h[1-6])\s*>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** A recorded video note, as distinct from a picture or a file. */
export const isVideoNote = (a: Attachment) => a.kind === "video" && Boolean(a.media_key || a.media_url);
/** A video the person picked rather than recorded: the server keeps a picked file's size, a recording its length. */
export const isPickedVideo = (a: Attachment) => isVideoNote(a) && Boolean(a.size);
/** Where a picture or file is fetched from. */
export const fileHref = (a: Attachment): string | null => a.media_url ?? (a.key ? assetUrl(a.key) : (a.url ?? null));

/** The short words that stand for an entry: in a reply bar, a pin, the Files tab. */
export function snippet(e: Entry | undefined): string {
  if (!e) return "";
  if (e.comment) return e.comment.body || e.comment.attachments?.[0]?.name || "";
  if (e.post) {
    const p = e.post;
    if (p.kind === "link") return p.link?.title || p.title || p.link?.domain || "";
    if (p.kind === "video") return p.title || `Video note${p.attachments[0]?.duration ? `, ${p.attachments[0].duration}` : ""}`;
    if (p.kind === "image") return p.title || "Photo";
    if (p.kind === "file") return p.title || p.attachments[0]?.name || "File";
    return p.title || plainText(p.body_html);
  }
  const m = e.message;
  if (!m) return "";
  if (m.body) return m.body;
  const a = m.attachments?.[0];
  if (!a) return "";
  if (isVideoNote(a)) return a.title || `Video note${a.duration ? `, ${a.duration}` : ""}`;
  return a.kind === "image" ? "Photo" : a.name;
}

// --- the files a pin can point at ---

export interface FileItem {
  key: string;
  title: string;
  /** Where it came from: an update, a message, or a talk. */
  from: string;
  href: string | null;
  /** A talk's handout: its interaction id, for the room's download count. */
  handout?: { webinar_id: string; interaction_id: string };
  entryId?: string;
  at: number;
}

export function filesOf(data: Pick<WatchPageResponse, "sessions">, convo: Conversation, nowSec: number, timeZone?: string): FileItem[] {
  const files: FileItem[] = [];
  const when = (at: number) => pinDay(at, nowSec, timeZone);
  for (const e of convo.byId.values()) {
    if (e.from !== "host" || e.kind === "comment") continue;
    const atts = e.post?.attachments ?? e.message?.attachments ?? [];
    for (const [i, a] of atts.entries()) {
      const key = `${e.id}:${i}`;
      if (a.kind !== "file" || files.some((f) => f.key === key)) continue;
      const title = e.post?.kind === "file" && e.post.attachments.length === 1 && e.post.title ? e.post.title : a.name;
      files.push({ key, title, from: `${e.post ? "In an update" : "In your chat"}, ${when(e.at)}`, href: fileHref(a), entryId: e.id, at: e.at });
    }
  }
  for (const s of data.sessions) {
    for (const i of s.interactions) {
      if (i.type !== "handout") continue;
      const c = i.config as HandoutConfig;
      files.push({
        key: `handout:${i.id}`,
        title: c.title || c.file,
        from: `From ${s.title}`,
        href: c.key ? assetUrl(c.key) : null,
        handout: { webinar_id: s.webinar_id, interaction_id: i.id },
        at: s.last_visit_at,
      });
    }
  }
  return files;
}

// --- Answers ---

/** Unanswered first, then the newest question. */
export function answersOf(answers: readonly WatchAnswer[]): WatchAnswer[] {
  return answers.toSorted((a, b) => Number(Boolean(a.answer)) - Number(Boolean(b.answer)) || b.asked_at - a.asked_at);
}

export const waitingAnswers = (answers: readonly WatchAnswer[]) => answers.filter((a) => !a.answer).length;

/** Where an answer was given, as its card says under it. */
export function answerSource(a: WatchAnswer, host: Host, rooms: readonly WatchSession[], nowSec: number, timeZone?: string): string {
  if (a.source === "signup") return "From your sign-up";
  const room = rooms.find((r) => r.webinar_id === a.webinar_id);
  if (a.source === "moment") return room ? `${host.first} asked in ${room.title}` : `${host.first} asked in a talk`;
  return `${host.first}’s question, ${pinDay(a.asked_at, nowSec, timeZone)}`;
}

// --- Sessions ---

type SessionDot = "came" | "part" | "missed" | "skip" | "nolive" | "live" | "saved" | "open";

/** One row of a series' timeline. */
export interface TimelineRow {
  key: string;
  session: WatchSeriesSession;
  kind: "past" | "next" | "future" | "skip";
  dot: SessionDot;
  /** What happened, or what it holds for them. */
  stat: string;
  /** The replay's way in, for a session that ran. */
  go: string | null;
}

export function timelineOf(room: WatchSession, nowMs: number): TimelineRow[] {
  const series = room.live?.series;
  if (!series) return [];
  const next = comingSession(series);
  return series.sessions.map((s): TimelineRow => {
    if (s.state === "ended") {
      const y = youWords(s);
      const ready = s.replay === "ready";
      return {
        key: s.id,
        session: s,
        kind: "past",
        dot: y.came && y.pct >= 100 ? "came" : y.pct > 0 || y.came ? "part" : "missed",
        stat: y.words,
        go: ready ? y.go : null,
      };
    }
    if (s.state === "skipped") return { key: s.id, session: s, kind: "skip", dot: "skip", stat: "Skipped. Your seat carried over.", go: null };
    if (s.state === "missed") return { key: s.id, session: s, kind: "past", dot: "nolive", stat: "This one didn't happen", go: null };
    if (s.state === "live") return { key: s.id, session: s, kind: "next", dot: "live", stat: "Live now", go: null };
    const isNext = next?.id === s.id;
    return { key: s.id, session: s, kind: isNext ? "next" : "future", dot: series.left ? "open" : "saved", stat: series.left ? "" : "Your seat is saved", go: null };
  }).filter((r) => !(r.kind === "skip" && Date.parse(r.session.startsAt) < nowMs - 30 * 86_400_000));
}

/** Where today falls in a timeline: before the first row that has not run yet. */
export const todayIndex = (rows: readonly TimelineRow[]) => {
  const i = rows.findIndex((r) => r.kind !== "past" && r.kind !== "skip");
  return i < 0 ? rows.length : i;
};

/** A month of a series' calendar: the blanks before the 1st, then each day and its session. */
interface MonthCell {
  day: number;
  /** "2026-11-03". */
  date: string;
  row: TimelineRow | null;
}
export function monthOf(rows: readonly TimelineRow[], year: number, month: number, timeZone: string): { lead: number; cells: MonthCell[] } {
  const len = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const onDay = new Map<string, TimelineRow>();
  for (const r of rows) {
    const at = wallClock(r.session.startsAt, timeZone);
    if (at && !onDay.has(at.date)) onDay.set(at.date, r);
  }
  const cells: MonthCell[] = [];
  for (let d = 1; d <= len; d++) {
    const date = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ day: d, date, row: onDay.get(date) ?? null });
  }
  return { lead: new Date(Date.UTC(year, month, 1)).getUTCDay(), cells };
}

/** The year and month a row falls in, on the series' clock. */
export function monthOfRow(row: TimelineRow, timeZone: string): { year: number; month: number } | null {
  const at = wallClock(row.session.startsAt, timeZone);
  if (!at) return null;
  const [y, m] = at.date.split("-").map(Number);
  return { year: y, month: m - 1 };
}

// --- Pinned: Continue ---

/** The recorded talk the person is partway through, the one touched last; else one not started yet. */
export function continueOf(rooms: readonly WatchSession[]): WatchSession | null {
  const talks = rooms.filter((r) => !r.live && r.duration_sec > 0);
  const partway = talks.filter((r) => r.watched_sec > 0 && r.watched_sec < r.duration_sec - 5);
  return partway[0] ?? talks.find((r) => r.watched_sec === 0) ?? null;
}

/** "12:04", "1:02:10". */
export function clock(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = String(s % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${r}` : `${m}:${r}`;
}

/** "35 min", "1 hour 5 min". */
export function talkLength(sec: number): string {
  const min = Math.max(1, Math.round(sec / 60));
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h} ${h === 1 ? "hour" : "hours"}${m ? ` ${m} min` : ""}`;
}

// --- pins ---

/** What a pin points at, found in the conversation; a handout pin points at a talk's file. */
export function pinTarget(pin: WatchPin, convo: Conversation, files: readonly FileItem[]): { entry?: Entry; file?: FileItem } {
  if (pin.target_kind === "handout") return { file: files.find((f) => f.handout?.interaction_id === pin.target_id) };
  return { entry: convo.byId.get(pin.target_id) };
}

/** The live pins whose target is on the page: a pin on something gone is not drawn. */
export function pinsOnPage(pins: readonly WatchPin[], convo: Conversation, files: readonly FileItem[]): WatchPin[] {
  return pins.filter((p) => {
    const t = pinTarget(p, convo, files);
    return Boolean(t.entry || t.file);
  });
}

/** The room a new message goes to: the room the person last had news from, else the room they joined last. */
export function defaultRoom(rooms: readonly WatchSession[]): string {
  let best: { id: string; at: number } | null = null;
  for (const r of rooms) {
    const at = Math.max(0, ...r.messages.filter((m) => m.sender === "host").map((m) => m.created_at), ...r.posts.map((p) => p.created_at));
    if (at > 0 && (!best || at > best.at)) best = { id: r.webinar_id, at };
  }
  return best?.id ?? rooms[0]?.webinar_id ?? "";
}
